import {
  COMPARE_LIMITS,
  decodeUtf8,
  normalizeNewlines,
  unifiedDiffText,
  validateCompareFile,
  validateCompareSource
} from "./compare-core.js";
import { parseXmlToNeutralTree } from "./compare-xml.js";
import "./compare-tool.css";

const root = document.querySelector("[data-compare-tool]");

if (root) {
  const kind = root.dataset.compareTool;
  const elements = Object.fromEntries([
    "compareStatus", "compareWorkspace", "compareOriginal", "compareChanged",
    "compareOriginalFile", "compareChangedFile", "chooseCompareOriginal",
    "chooseCompareChanged", "clearCompareOriginal", "clearCompareChanged",
    "compareOriginalMeta", "compareChangedMeta", "compareOriginalCount",
    "compareChangedCount", "swapCompareSides", "clearCompareAll", "runCompare",
    "ignoreCompareCase", "ignoreCompareWhitespace", "compareResult",
    "compareResultTitle", "compareSummary", "compareResultBody", "showSideBySide",
    "showUnified", "copyCompareOriginal", "copyCompareChanged", "copyCompareResult",
    "copyCompareOriginalHint", "copyCompareChangedHint"
  ].map(id => [id, document.getElementById(id)]));
  const state = {
    worker: null,
    requestId: 0,
    renderId: 0,
    result: null,
    view: "side",
    fileNames: { left: "", right: "" },
    busy: false
  };

  const showStatus = (message, kindName = "error") => {
    elements.compareStatus.textContent = message;
    elements.compareStatus.dataset.kind = kindName;
    elements.compareStatus.hidden = !message;
    if (message && kindName === "error")
      window.toolboxAnalytics?.track("tool_error", { error_category: "validation" });
  };

  const compareMode = () => root.querySelector('input[name="compareMode"]:checked')?.value || "exact";

  const updateCounts = () => {
    updateCount(elements.compareOriginalCount, elements.compareOriginal.value);
    updateCount(elements.compareChangedCount, elements.compareChanged.value);
  };

  const updateCopyLabels = () => {
    const normalized = kind !== "text" && compareMode() === "format";
    elements.copyCompareOriginal.textContent = normalized ? "Copy normalized original" : "Copy original";
    elements.copyCompareChanged.textContent = normalized ? "Copy normalized changed" : "Copy changed";
    const originalHint = normalized
      ? "Copy the original after consistent formatting used for this comparison."
      : "Copy the original content used for this comparison.";
    const changedHint = normalized
      ? "Copy the changed version after consistent formatting used for this comparison."
      : "Copy the changed content used for this comparison.";
    elements.copyCompareOriginal.dataset.tooltip = originalHint;
    elements.copyCompareChanged.dataset.tooltip = changedHint;
    elements.copyCompareOriginalHint.textContent = originalHint;
    elements.copyCompareChangedHint.textContent = changedHint;
  };

  const clearResult = () => {
    state.renderId += 1;
    state.result = null;
    elements.compareResult.hidden = true;
    elements.compareResultBody.replaceChildren();
    updateCopyLabels();
  };

  const inputChanged = () => {
    showStatus("");
    updateCounts();
    clearResult();
  };

  const setBusy = busy => {
    state.busy = busy;
    const controls = root.querySelectorAll("button, input, textarea");
    for (const control of controls) control.disabled = busy;
    elements.runCompare.textContent = busy ? "Comparing…" : "Compare";
    root.setAttribute("aria-busy", String(busy));
  };

  const loadFile = async (file, side) => {
    const validation = validateCompareFile(file, kind);
    if (validation) return showStatus(validation);
    try {
      const text = decodeUtf8(new Uint8Array(await file.arrayBuffer()));
      const sourceValidation = validateCompareSource(text);
      if (sourceValidation) throw new Error(sourceValidation);
      const editor = side === "left" ? elements.compareOriginal : elements.compareChanged;
      const meta = side === "left" ? elements.compareOriginalMeta : elements.compareChangedMeta;
      editor.value = text;
      state.fileNames[side] = file.name;
      meta.textContent = file.name;
      meta.hidden = false;
      inputChanged();
      editor.focus();
    } catch (error) {
      showStatus(error?.message || "This file could not be read.");
    }
  };

  const clearSide = side => {
    const editor = side === "left" ? elements.compareOriginal : elements.compareChanged;
    const meta = side === "left" ? elements.compareOriginalMeta : elements.compareChangedMeta;
    editor.value = "";
    state.fileNames[side] = "";
    meta.textContent = "";
    meta.hidden = true;
    inputChanged();
    editor.focus();
  };

  const createWorker = () => {
    if (state.worker) return state.worker;
    state.worker = new Worker("/dist/compare-worker.js", { type: "module" });
    return state.worker;
  };

  const runComparison = async () => {
    if (state.busy) return;
    showStatus("");
    const left = normalizeNewlines(elements.compareOriginal.value);
    const right = normalizeNewlines(elements.compareChanged.value);
    for (const [side, source] of [["Original", left], ["Changed", right]]) {
      const error = validateCompareSource(source);
      if (error) return showStatus(`${side}: ${error}`);
    }
    if (!left && !right) return showStatus("Enter or load content on at least one side.");

    const mode = compareMode();
    const payload = {
      kind,
      mode,
      left,
      right,
      ignoreCase: Boolean(elements.ignoreCompareCase?.checked),
      ignoreWhitespace: Boolean(elements.ignoreCompareWhitespace?.checked)
    };
    if (kind === "xml" && mode === "format") {
      try {
        payload.leftTree = parseXmlToNeutralTree(left, "Original");
        payload.rightTree = parseXmlToNeutralTree(right, "Changed");
      } catch (error) {
        return showStatus(error?.message || "The XML could not be parsed.");
      }
    }

    setBusy(true);
    clearResult();
    const requestId = ++state.requestId;
    try {
      const result = await new Promise((resolve, reject) => {
        const worker = createWorker();
        const onMessage = event => {
          if (event.data?.requestId !== requestId) return;
          worker.removeEventListener("message", onMessage);
          worker.removeEventListener("error", onError);
          if (event.data.error) reject(new Error(event.data.error));
          else resolve(event.data.result);
        };
        const onError = () => {
          worker.removeEventListener("message", onMessage);
          worker.removeEventListener("error", onError);
          reject(new Error("The comparison worker stopped unexpectedly."));
        };
        worker.addEventListener("message", onMessage);
        worker.addEventListener("error", onError);
        worker.postMessage({ requestId, payload });
      });
      state.result = result;
      state.view = "side";
      elements.showSideBySide.setAttribute("aria-pressed", "true");
      elements.showUnified.setAttribute("aria-pressed", "false");
      renderSummary(result);
      elements.compareResult.hidden = false;
      await renderResultView();
      elements.compareResultTitle.focus();
      window.toolboxAnalytics?.track("tool_complete");
    } catch (error) {
      showStatus(error?.message || "The comparison could not be completed.");
      window.toolboxAnalytics?.track("tool_error", { error_category: "processing" });
    } finally {
      setBusy(false);
    }
  };

  const renderSummary = result => {
    const summary = result.summary;
    const message = result.identical
      ? "No differences found."
      : `${summary.changed} changed, ${summary.added} added, and ${summary.removed} removed ${pluralize("line", summary.changed + summary.added + summary.removed)}.`;
    elements.compareResultTitle.textContent = result.identical ? "The content matches" : "Comparison result";
    elements.compareSummary.replaceChildren(
      summaryItem("Changed", summary.changed, "changed"),
      summaryItem("Added", summary.added, "added"),
      summaryItem("Removed", summary.removed, "removed"),
      summaryItem("Unchanged", summary.equal, "equal")
    );
    elements.compareSummary.setAttribute("aria-label", message);
  };

  const renderResultView = async () => {
    if (!state.result) return;
    const renderId = ++state.renderId;
    const container = state.view === "side"
      ? buildSideBySideShell()
      : buildUnifiedShell();
    elements.compareResultBody.replaceChildren(container.shell);
    const rows = state.result.rows;
    for (let start = 0; start < rows.length; start += 300) {
      if (renderId !== state.renderId) return;
      const fragment = document.createDocumentFragment();
      for (const row of rows.slice(start, start + 300)) {
        if (state.view === "side") fragment.append(buildSideRow(row));
        else fragment.append(...buildUnifiedRows(row));
      }
      container.body.append(fragment);
      if (start + 300 < rows.length) await nextFrame();
    }
    if (!rows.length) {
      const empty = document.createElement("p");
      empty.className = "compare-empty-result";
      empty.textContent = "Both sides are empty after normalization.";
      container.body.append(empty);
    }
  };

  const switchView = async view => {
    if (!state.result || state.view === view) return;
    state.view = view;
    elements.showSideBySide.setAttribute("aria-pressed", String(view === "side"));
    elements.showUnified.setAttribute("aria-pressed", String(view === "unified"));
    await renderResultView();
  };

  const copyValue = async target => {
    if (!state.result) return;
    const value = target === "left"
      ? state.result.normalizedLeft
      : target === "right"
        ? state.result.normalizedRight
        : unifiedDiffText(state.result);
    try {
      await navigator.clipboard.writeText(value);
      showStatus("Copied to the clipboard.", "success");
    } catch {
      showStatus("Clipboard access was unavailable. Select and copy the content manually.");
    }
  };

  elements.compareOriginal.addEventListener("input", inputChanged);
  elements.compareChanged.addEventListener("input", inputChanged);
  root.querySelectorAll('input[name="compareMode"]').forEach(input => input.addEventListener("change", inputChanged));
  elements.ignoreCompareCase?.addEventListener("change", inputChanged);
  elements.ignoreCompareWhitespace?.addEventListener("change", inputChanged);
  elements.chooseCompareOriginal.addEventListener("click", () => elements.compareOriginalFile.click());
  elements.chooseCompareChanged.addEventListener("click", () => elements.compareChangedFile.click());
  elements.compareOriginalFile.addEventListener("change", event => {
    const file = Array.from(event.target.files || [])[0];
    event.target.value = "";
    if (file) void loadFile(file, "left");
  });
  elements.compareChangedFile.addEventListener("change", event => {
    const file = Array.from(event.target.files || [])[0];
    event.target.value = "";
    if (file) void loadFile(file, "right");
  });
  elements.clearCompareOriginal.addEventListener("click", () => clearSide("left"));
  elements.clearCompareChanged.addEventListener("click", () => clearSide("right"));
  elements.swapCompareSides.addEventListener("click", () => {
    [elements.compareOriginal.value, elements.compareChanged.value] = [elements.compareChanged.value, elements.compareOriginal.value];
    [state.fileNames.left, state.fileNames.right] = [state.fileNames.right, state.fileNames.left];
    setFileMeta(elements.compareOriginalMeta, state.fileNames.left);
    setFileMeta(elements.compareChangedMeta, state.fileNames.right);
    inputChanged();
  });
  elements.clearCompareAll.addEventListener("click", () => {
    elements.compareOriginal.value = "";
    elements.compareChanged.value = "";
    state.fileNames = { left: "", right: "" };
    setFileMeta(elements.compareOriginalMeta, "");
    setFileMeta(elements.compareChangedMeta, "");
    inputChanged();
    elements.compareOriginal.focus();
  });
  elements.runCompare.addEventListener("click", runComparison);
  elements.showSideBySide.addEventListener("click", () => switchView("side"));
  elements.showUnified.addEventListener("click", () => switchView("unified"));
  elements.copyCompareOriginal.addEventListener("click", () => copyValue("left"));
  elements.copyCompareChanged.addEventListener("click", () => copyValue("right"));
  elements.copyCompareResult.addEventListener("click", () => copyValue("result"));

  for (const side of ["left", "right"]) {
    const editor = side === "left" ? elements.compareOriginal : elements.compareChanged;
    const dropTarget = editor.closest("[data-compare-drop]");
    dropTarget.addEventListener("dragover", event => {
      event.preventDefault();
      dropTarget.classList.add("is-dragging");
    });
    dropTarget.addEventListener("dragleave", () => dropTarget.classList.remove("is-dragging"));
    dropTarget.addEventListener("drop", event => {
      event.preventDefault();
      dropTarget.classList.remove("is-dragging");
      const file = Array.from(event.dataTransfer?.files || [])[0];
      if (file) void loadFile(file, side);
    });
  }

  updateCounts();
  updateCopyLabels();
  window.addEventListener("pagehide", () => state.worker?.terminate());
}

function updateCount(element, value) {
  const text = normalizeNewlines(value);
  const lines = text ? text.split("\n").length : 0;
  element.textContent = `${text.length.toLocaleString("en-US")} / ${COMPARE_LIMITS.characters.toLocaleString("en-US")} characters · ${lines.toLocaleString("en-US")} ${pluralize("line", lines)}`;
}

function pluralize(word, count) {
  return count === 1 ? word : `${word}s`;
}

function setFileMeta(element, value) {
  element.textContent = value;
  element.hidden = !value;
}

function summaryItem(label, value, kind) {
  const item = document.createElement("span");
  item.className = `compare-summary-item is-${kind}`;
  const strong = document.createElement("strong");
  strong.textContent = String(value);
  item.append(strong, ` ${label}`);
  return item;
}

function buildSideBySideShell() {
  const shell = document.createElement("div");
  shell.className = "compare-diff-table compare-side-table";
  shell.setAttribute("role", "table");
  shell.setAttribute("aria-label", "Side-by-side comparison");
  const header = document.createElement("div");
  header.className = "compare-diff-header";
  header.setAttribute("role", "row");
  for (const label of ["Original", "Changed"]) {
    const cell = document.createElement("div");
    cell.setAttribute("role", "columnheader");
    cell.textContent = label;
    header.append(cell);
  }
  const body = document.createElement("div");
  body.setAttribute("role", "rowgroup");
  shell.append(header, body);
  return { shell, body };
}

function buildSideRow(row) {
  const result = document.createElement("div");
  result.className = `compare-side-row is-${row.status}`;
  result.setAttribute("role", "row");
  result.append(
    buildCodeCell(row.leftNumber, row.leftSegments, row.status === "added" ? "" : statusMarker(row.status, "left")),
    buildCodeCell(row.rightNumber, row.rightSegments, row.status === "removed" ? "" : statusMarker(row.status, "right"))
  );
  return result;
}

function buildUnifiedShell() {
  const shell = document.createElement("div");
  shell.className = "compare-diff-table compare-unified-table";
  shell.setAttribute("role", "table");
  shell.setAttribute("aria-label", "Unified comparison");
  const header = document.createElement("div");
  header.className = "compare-unified-header";
  header.setAttribute("role", "columnheader");
  header.textContent = "Original → Changed";
  const body = document.createElement("div");
  body.setAttribute("role", "rowgroup");
  shell.append(header, body);
  return { shell, body };
}

function buildUnifiedRows(row) {
  if (row.status === "changed") {
    return [
      buildUnifiedRow("removed", row.leftNumber, null, row.leftSegments),
      buildUnifiedRow("added", null, row.rightNumber, row.rightSegments)
    ];
  }
  if (row.status === "removed") return [buildUnifiedRow("removed", row.leftNumber, null, row.leftSegments)];
  if (row.status === "added") return [buildUnifiedRow("added", null, row.rightNumber, row.rightSegments)];
  return [buildUnifiedRow("equal", row.leftNumber, row.rightNumber, row.leftSegments)];
}

function buildUnifiedRow(status, leftNumber, rightNumber, segments) {
  const result = document.createElement("div");
  result.className = `compare-unified-row is-${status}`;
  result.setAttribute("role", "row");
  const left = document.createElement("span");
  left.className = "compare-line-number";
  left.textContent = leftNumber ?? "";
  const right = document.createElement("span");
  right.className = "compare-line-number";
  right.textContent = rightNumber ?? "";
  const marker = document.createElement("span");
  marker.className = "compare-line-marker";
  marker.textContent = status === "added" ? "+" : status === "removed" ? "−" : " ";
  const code = document.createElement("code");
  appendSegments(code, segments);
  result.append(left, right, marker, code);
  return result;
}

function buildCodeCell(number, segments, markerText) {
  const cell = document.createElement("div");
  cell.className = "compare-code-cell";
  cell.setAttribute("role", "cell");
  const line = document.createElement("span");
  line.className = "compare-line-number";
  line.textContent = number ?? "";
  const marker = document.createElement("span");
  marker.className = "compare-line-marker";
  marker.textContent = markerText;
  const code = document.createElement("code");
  appendSegments(code, segments);
  cell.append(line, marker, code);
  return cell;
}

function appendSegments(element, segments) {
  for (const segment of segments) {
    if (segment.changed) {
      const mark = document.createElement("mark");
      mark.textContent = segment.text;
      element.append(mark);
    } else {
      element.append(document.createTextNode(segment.text));
    }
  }
}

function statusMarker(status, side) {
  if (status === "changed") return side === "left" ? "−" : "+";
  if (status === "removed") return "−";
  if (status === "added") return "+";
  return " ";
}

function nextFrame() {
  return new Promise(resolve => requestAnimationFrame(resolve));
}
